import {
  Button,
  Caption1,
  makeStyles,
  SelectTabData,
  SelectTabEvent,
  Spinner,
  Tab,
  TabList,
  tokens,
} from '@fluentui/react-components';
import ItemsTable from '@modules/items/components/ItemsTable/ItemsTable';
import { Pagination } from '@shared/components/Pagination';

import { DASHBOARD_CARD_APPEARANCE_STYLE } from '../dashboard.styles';
import { getDashboardSegmentEmptyMessage } from '../helpers/dashboardPresentation';
import { getDashboardItemsTableConfig } from '../helpers/dashboardSegmentColumns';
import { EDashboardSegmentKey } from '../types/dashboard.types';
import type { TDashboardSegmentPageView, TDashboardSegmentView } from '../types/dashboard.types';

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  frame: {
    ...DASHBOARD_CARD_APPEARANCE_STYLE,
    overflow: 'hidden',
  },
  tabsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  tabs: {
    overflowX: 'auto',
    minWidth: 0,
  },
  caption: {
    color: tokens.colorNeutralForeground3,
  },
  tableScroller: {
    overflowX: 'auto',
    '& table': {
      minWidth: '1120px',
      tableLayout: 'fixed',
    },
  },
  stateMessage: {
    display: 'block',
    padding: tokens.spacingHorizontalXL,
    color: tokens.colorNeutralForeground3,
  },
  errorAction: {
    marginLeft: tokens.spacingHorizontalM,
  },
});

const getShownLabel = (segment: TDashboardSegmentPageView): string => {
  if (segment.totalCount === 0) return '0 items';
  if (segment.items.length === 0) return `0 of ${segment.totalCount.toLocaleString()} shown`;
  const first = (segment.page - 1) * segment.pageSize + 1;
  const last = Math.min(first + segment.items.length - 1, segment.totalCount);
  return `Showing ${first.toLocaleString()}-${last.toLocaleString()} of ${segment.totalCount.toLocaleString()}`;
};

const LOADING_ITEMS_MESSAGE = 'Loading items...';

type TProps = {
  segments: TDashboardSegmentView[];
  selection: {
    key: EDashboardSegmentKey;
    onChange: (segmentKey: EDashboardSegmentKey) => void;
  };
  pageState: {
    segmentPage?: TDashboardSegmentPageView;
    isLoading: boolean;
    isFetching: boolean;
    error: unknown;
    refetch: () => void;
  };
  pagination: {
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
};

const DashboardItemSegmentsTable = ({ segments, selection, pageState, pagination }: TProps) => {
  const { key: selectedSegmentKey, onChange: onSelectedSegmentKeyChange } = selection;
  const { segmentPage, isLoading, isFetching, error, refetch } = pageState;
  const { page, pageSize, onPageChange, onPageSizeChange } = pagination;
  const selectedSegment = segments.find((segment) => segment.key === selectedSegmentKey) ?? segments[0];
  const classes = useStyles();

  if (!selectedSegment) {
    return (
      <div className={classes.frame}>
        <Caption1 className={classes.stateMessage}>No item segments are available for the current view.</Caption1>
      </div>
    );
  }

  const selectedSegmentPage = segmentPage?.key === selectedSegment.key ? segmentPage : undefined;
  const selectedTotalCount = selectedSegmentPage?.totalCount ?? selectedSegment.totalCount;
  const tableConfig = getDashboardItemsTableConfig(selectedSegment.key);

  const handleTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
    onSelectedSegmentKeyChange(data.value as EDashboardSegmentKey);
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.tabsRow}>
        <div className={classes.tabs}>
          <TabList size="medium" selectedValue={selectedSegment.key} onTabSelect={handleTabSelect}>
            {segments.map((segment) => (
              <Tab key={segment.key} value={segment.key}>
                {segment.label} (
                {(segment.key === selectedSegmentKey ? selectedTotalCount : segment.totalCount).toLocaleString()})
              </Tab>
            ))}
          </TabList>
        </div>
        <Caption1 className={classes.caption}>
          {selectedSegmentPage ? getShownLabel(selectedSegmentPage) : isLoading ? LOADING_ITEMS_MESSAGE : ''}
        </Caption1>
      </div>

      {isFetching && selectedSegmentPage ? (
        <Spinner size="tiny" aria-label={`Refreshing ${selectedSegment.label}`} />
      ) : null}
      {error && !selectedSegmentPage ? (
        <div className={classes.frame}>
          <div className={classes.stateMessage}>
            <Caption1>Loading this item segment failed!</Caption1>
            <Button className={classes.errorAction} appearance="secondary" size="small" onClick={refetch}>
              Retry
            </Button>
          </div>
        </div>
      ) : isLoading && !selectedSegmentPage ? (
        <div className={classes.frame}>
          <Caption1 className={classes.stateMessage}>{LOADING_ITEMS_MESSAGE}</Caption1>
        </div>
      ) : !selectedSegmentPage || selectedSegmentPage.items.length === 0 ? (
        <div className={classes.frame}>
          <Caption1 className={classes.stateMessage}>{getDashboardSegmentEmptyMessage(selectedSegment.key)}</Caption1>
        </div>
      ) : (
        <div className={classes.tableScroller}>
          <ItemsTable
            ariaLabel={`${selectedSegment.label} items`}
            rows={selectedSegmentPage.items.map(({ item, root, activeApproval }) => ({
              key: `${item.type}-${item._id}`,
              item,
              rootName: root.name,
              approvalDateOpened: activeApproval?.dateOpened,
            }))}
            show={tableConfig.show}
            labels={tableConfig.labels}
          />
        </div>
      )}

      {selectedSegmentPage ? (
        <Pagination
          ariaLabel={`${selectedSegment.label} segment pagination`}
          page={page}
          pageSize={pageSize}
          total={selectedSegmentPage.totalCount}
          isFetching={isFetching}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      ) : null}
    </div>
  );
};

export default DashboardItemSegmentsTable;
